// @ts-nocheck

import { Location, locationName, locationUserPermission } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import type FeatureFlags from '@cobliteam/hooks/src/useFeatureFlags/featureFlags';
import { type Permission, usePermissions } from '@cobliteam/permissions';
import AccessDenied from '@cobliteam/ui-business/components/AccessDenied';
import type { ComponentType } from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import MaintenancePage from '../../pages/MaintenancePage';

import routes, { type IRoute } from '../../routes';

interface PrivateRouteProps {
  route: IRoute;
}

const recursivePaths = (routesDef: IRoute[]) => {
  const paths: IRoute[] = [];
  routesDef.map((r: IRoute) => {
    paths.push(r);
    if (r.routes) paths.push(...recursivePaths(r.routes));
  });
  return paths;
};

const PrivateRoute = ({ route }: PrivateRouteProps) => {
  const { isAccessAllowed } = usePermissions();
  const { hasGroupsRestriction } = useUser();
  const location = useLocation();
  const featureToggleFlags = useFeatureFlags();

  const routerSelected: IRoute | undefined = recursivePaths(routes).find((r: IRoute) => {
    const pathRegex = new RegExp(`^${r.path?.replace(/:[a-zA-Z]+/g, '[a-zA-Z0-9-]+')}$`);
    return pathRegex.test(location.pathname);
  });

  const requiredPermission = locationUserPermission[routerSelected?.path as Location] as Permission;
  const isAllowed = isAccessAllowed(requiredPermission);
  const userHasGroupsRestriction = route.hasGroupsRestriction && hasGroupsRestriction === true;

  // check if the router is in the maintenance feature flag
  const isMaintenance =
    Array.isArray(featureToggleFlags.maintenance) &&
    featureToggleFlags.maintenance.some((router) => {
      return router.url === routerSelected?.path;
    });

  const publicRoutes: Location[] = [Location.LOGIN, Location.TOKEN_VALIDATION, Location.PASSWORD_RECOVERY, Location.PASSWORD_RESET, Location.MFA_CHALLENGE];

  // redirect to home / if path is empty
  if (location.pathname === Location.EMPTY) return <Redirect to={Location.HOME} />;

  if (routerSelected?.featureFlag && !!featureToggleFlags[routerSelected.featureFlag as FeatureFlags] === false) return <Redirect to={Location.NOT_FOUND} />;

  // after login and router is public - hotfix
  if (!routerSelected && publicRoutes.includes(location.pathname as Location)) return <Redirect to={Location.HOME} />;

  // router not found
  if (!routerSelected) return <Redirect to={Location.NOT_FOUND} />;

  // change title
  if (location.pathname === routerSelected?.path) {
    document.title = `Cobli - ${routerSelected.title || locationName[routerSelected.path as Location] || ''}`;
  }

  return (
    <Route
      path={route.path}
      render={(props) => {
        if (!isAllowed) return <AccessDenied pageDescription={routerSelected?.accessDeniedDescription} />;
        if (userHasGroupsRestriction) return <AccessDenied />;
        if (isMaintenance) return <MaintenancePage title={`${routerSelected.title || locationName[routerSelected.path as Location] || ''}`} />;
        const Component = route.component as ComponentType<any>; // Cast route.component as ComponentType
        return <Component {...props} routes={route.routes} />;
      }}
      exact={route.exact}
    />
  );
};

export default PrivateRoute;
